import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Zap, Battery, Clock, CheckCircle } from "lucide-react";
import { CHARGER_IMAGES, CHARGER_PRODUCTS } from "../constants/chargerImages";

const ChargersShowcase = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });
  const [activeTab, setActiveTab] = useState("dc");

  const products = activeTab === "dc" ? CHARGER_PRODUCTS.dc : CHARGER_PRODUCTS.ac;

  return (
    <section
      id="chargers"
      className="py-24 md:py-32 bg-background relative overflow-hidden"
      data-testid="chargers-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[200px] -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Our Equipment
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            PREMIUM
            <span className="text-primary"> CHARGERS</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            Powered by Zeus series chargers - industry-leading EV charging equipment
            with 95%+ efficiency and built for Indian conditions.
          </p>
        </motion.div>

        {/* Tab Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex justify-center gap-4 mb-12"
        >
          <button
            onClick={() => setActiveTab("dc")}
            className={`px-8 py-3 rounded-full font-medium transition-all ${
              activeTab === "dc"
                ? "bg-primary text-black shadow-[0_0_20px_rgba(204,255,0,0.3)]"
                : "bg-white/5 text-foreground-muted hover:bg-white/10 border border-white/10"
            }`}
            data-testid="tab-dc-chargers"
          >
            <Zap className="w-4 h-4 inline mr-2" />
            DC Fast Chargers
          </button>
          <button
            onClick={() => setActiveTab("ac")}
            className={`px-8 py-3 rounded-full font-medium transition-all ${
              activeTab === "ac"
                ? "bg-secondary text-black shadow-[0_0_20px_rgba(0,212,255,0.3)]"
                : "bg-white/5 text-foreground-muted hover:bg-white/10 border border-white/10"
            }`}
            data-testid="tab-ac-chargers"
          >
            <Battery className="w-4 h-4 inline mr-2" />
            AC Chargers
          </button>
        </motion.div>

        {/* Charger Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, index) => (
            <motion.div
              key={product.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              className="glass-card group hover:border-primary/50 transition-all overflow-hidden"
              data-testid={`charger-card-${index}`}
            >
              {/* Image */}
              <div className="relative h-48 -mx-8 -mt-8 mb-4 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-paper via-transparent to-transparent" />
                
                {/* Power Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-primary text-black font-bold text-sm">
                  {product.power}
                </div>
              </div>

              {/* Content */}
              <div>
                <h3 className="font-heading text-xl text-foreground">{product.name}</h3>
                <p className="text-foreground-muted text-sm mt-1">{product.description}</p>

                {/* Features */}
                <div className="mt-4 space-y-2">
                  {product.chargingTime && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-secondary" />
                      <span className="text-foreground-muted">{product.chargingTime}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="text-foreground-muted">95%+ Efficiency</span>
                  </div>
                </div>

                {/* Price */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-foreground-muted text-xs">Starting from</p>
                  <p className="font-heading text-2xl text-primary">Rs {product.price}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-foreground-muted mb-4">
            Interested in our chargers for your business or franchise?
          </p>
          <a
            href="#franchise"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            data-testid="chargers-cta"
          >
            Become a Franchise Partner
            <Zap className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Installation Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-20"
        >
          <h3 className="font-heading text-2xl text-foreground text-center mb-8">
            Installed Across <span className="text-primary">India</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(CHARGER_IMAGES.installations).map(([key, src], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="relative rounded-xl overflow-hidden aspect-square group"
              >
                <img
                  src={src}
                  alt={`Installation ${key}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  <span className="text-white text-xs font-medium capitalize">{key}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ChargersShowcase;
